import { access, mkdir, readFile } from 'node:fs/promises'
import { homedir } from 'node:os'
import { join } from 'node:path'
import { PACKAGE_VERSION } from '../version.js'
import { combinedNpxArgs } from './agent-config.js'

export type DoctorStatus = 'pass' | 'warn' | 'fail' | 'skip'

export interface DoctorCheck {
  id: string
  label: string
  status: DoctorStatus
  detail: string
  fix?: string
}

export interface DoctorOptions {
  apiKey?: string
  apiUrl?: string
  outputDir?: string
  fetchImpl?: typeof fetch
}

export interface DoctorOutput {
  ok: boolean
  version: string
  npmLatest: string | null
  checks: DoctorCheck[]
  recommendedConfig: {
    command: 'npx'
    args: string[]
    env: Record<string, string>
  }
}

function status(ok: boolean, warn = false): DoctorStatus {
  if (ok) return 'pass'
  return warn ? 'warn' : 'fail'
}

function parseMajor(version: string): number {
  return Number(version.replace(/^v/, '').split('.')[0] ?? 0)
}

async function readKeyFile(): Promise<string | null> {
  const path = process.env.MCP_SCRAPER_KEY_PATH?.trim() || join(homedir(), '.mcp-scraper-key')
  try {
    const value = (await readFile(path, 'utf8')).trim()
    return value || null
  } catch {
    return null
  }
}

async function npmLatest(fetchImpl: typeof fetch): Promise<string | null> {
  try {
    const res = await fetchImpl('https://registry.npmjs.org/mcp-scraper/latest', {
      signal: AbortSignal.timeout(5_000),
    })
    if (!res.ok) return null
    const data = await res.json() as { version?: string }
    return data.version ?? null
  } catch {
    return null
  }
}

export async function runDoctor(options: DoctorOptions = {}): Promise<DoctorOutput> {
  const fetchImpl = options.fetchImpl ?? fetch
  const apiUrl = (options.apiUrl ?? process.env.MCP_SCRAPER_API_URL ?? 'https://mcpscraper.dev').replace(/\/$/, '')
  const outputDir = options.outputDir ?? process.env.MCP_SCRAPER_OUTPUT_DIR ?? join(homedir(), 'Downloads', 'mcp-scraper')
  const configuredKey = options.apiKey?.trim() || process.env.MCP_SCRAPER_API_KEY?.trim() || await readKeyFile()
  const latest = await npmLatest(fetchImpl)

  const checks: DoctorCheck[] = []
  const nodeMajor = parseMajor(process.version)
  checks.push({
    id: 'node',
    label: 'Node.js',
    status: status(nodeMajor >= 20),
    detail: process.version,
    fix: nodeMajor >= 20 ? undefined : 'Install Node.js 20 or newer.',
  })

  checks.push({
    id: 'package_version',
    label: 'Local package',
    status: latest && latest !== PACKAGE_VERSION ? 'warn' : 'pass',
    detail: latest ? `local ${PACKAGE_VERSION}, npm latest ${latest}` : `local ${PACKAGE_VERSION}, npm latest unavailable`,
    fix: latest && latest !== PACKAGE_VERSION ? 'Use mcp-scraper@latest and restart the MCP client.' : undefined,
  })

  checks.push({
    id: 'api_key',
    label: 'API key',
    status: configuredKey ? 'pass' : 'warn',
    detail: configuredKey ? 'configured' : 'not configured',
    fix: configuredKey ? undefined : 'Set MCP_SCRAPER_API_KEY or pass --api-key.',
  })

  try {
    await mkdir(outputDir, { recursive: true })
    await access(outputDir)
    checks.push({ id: 'output_dir', label: 'Output directory', status: 'pass', detail: outputDir })
  } catch (err) {
    checks.push({
      id: 'output_dir',
      label: 'Output directory',
      status: 'fail',
      detail: outputDir,
      fix: err instanceof Error ? err.message : 'Create a writable output directory.',
    })
  }

  if (configuredKey) {
    try {
      const res = await fetchImpl(`${apiUrl}/me`, {
        headers: { 'x-api-key': configuredKey },
        signal: AbortSignal.timeout(8_000),
      })
      checks.push({
        id: 'api_reachability',
        label: 'Hosted API',
        status: res.ok ? 'pass' : 'fail',
        detail: `${apiUrl}/me returned ${res.status}`,
        fix: res.ok ? undefined : 'Verify the API key and account status.',
      })
    } catch (err) {
      checks.push({
        id: 'api_reachability',
        label: 'Hosted API',
        status: 'fail',
        detail: err instanceof Error ? err.message : String(err),
        fix: 'Check network access and MCP_SCRAPER_API_URL.',
      })
    }
  } else {
    checks.push({
      id: 'api_reachability',
      label: 'Hosted API',
      status: 'skip',
      detail: 'skipped because no API key is configured',
    })
  }

  checks.push({
    id: 'mcp_config',
    label: 'MCP command',
    status: 'pass',
    detail: `npx ${combinedNpxArgs().join(' ')}`,
    fix: 'Restart the MCP client after package updates.',
  })

  return {
    ok: checks.every(check => check.status === 'pass' || check.status === 'skip'),
    version: PACKAGE_VERSION,
    npmLatest: latest,
    checks,
    recommendedConfig: {
      command: 'npx',
      args: combinedNpxArgs(),
      env: { MCP_SCRAPER_API_KEY: configuredKey ? '$MCP_SCRAPER_API_KEY' : 'sk_live_your_key' },
    },
  }
}

export function renderDoctor(output: DoctorOutput): string {
  const icon: Record<DoctorStatus, string> = { pass: 'PASS', warn: 'WARN', fail: 'FAIL', skip: 'SKIP' }
  const lines = [
    `mcp-scraper doctor v${output.version}`,
    '',
    ...output.checks.map(check => {
      const fix = check.fix ? `\n    Fix: ${check.fix}` : ''
      return `[${icon[check.status]}] ${check.label}: ${check.detail}${fix}`
    }),
    '',
    `Recommended MCP command: npx ${output.recommendedConfig.args.join(' ')}`,
  ]
  return lines.join('\n')
}

